import {LitElement, type PropertyValues, type TemplateResult} from "lit";
import {GLOBAL} from "./globals";
import type {Constructor} from "./lifetime";
import type {Maybe, Nullable} from "../bootstrap/common/types";
import {resolve} from "../container";
import {Resolution} from "./resolution";

export function countReverseBackgroundAncestors(element: Element): number {
    let count = 0;
    let current: Element | null = element.parent();
    while (current && current !== GLOBAL.bodyElement()) {
        if (current.classList.contains("reverse-background") || current.hasAttribute("baseplate-enabled")) {
            count++;
        }
        current = current.parent();
    }
    return count;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function ShadowHighlightContextAware<T>(Base: T): T {
    abstract class ShadowHighlightMixin extends (Base as any) {

        protected constructor(
            private resolution: Resolution = resolve(Resolution)
        ) {
            super();
        }

        public connectedCallback(): void {
            super.connectedCallback();
            this.markHighLightingLevel();
            this.resolution.onWindowResize(() => this.markHighLightingLevel());
            this.markHighLightingLevel();
        }

        private markHighLightingLevel(): void {
            const element = this as unknown as Element;
            const count = countReverseBackgroundAncestors(element);
            const level = count === 0
                ? "initial"
                : count % 2 === 1
                    ? "highlight"
                    : "highlight-reverse";
            element.setAttribute("highlight-level", level);
        }
    }

    return ShadowHighlightMixin as unknown as T;
}

export class UnLitElement extends LitElement {
    protected createRenderRoot(): HTMLElement | DocumentFragment {
        const styles = (this.constructor as any).styles;
        if (styles) {
            const style = GLOBAL.document().createElement("style");
            style.innerText = styles.cssText;
            this.appendChild(style);
        }
        return this;
    }
}

export abstract class ConditionalUnLitElement extends UnLitElement {
    protected abstract shouldRender(): boolean;

    protected abstract renderTemplate(): TemplateResult;

    public render(): Nullable<TemplateResult> {
        if (!this.shouldRender()) {
            this.remove();
            return null;
        }
        return this.renderTemplate();
    }
}

export interface Initializable {
    initialized: () => void;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function Initializing<T extends Constructor<LitElement>>(Base: T): T & Constructor<Initializable> {
    return class extends Base implements Initializable {

        public initialized(): void {
            this.classList.remove("initializing");
        }
    };
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function AutoInitializing<T extends Constructor<LitElement>>(Base: T): T & Constructor<Initializable> {
    return class extends Base implements Initializable {

        protected firstUpdated(_changedProperties: PropertyValues): void {
            super.firstUpdated(_changedProperties);
            this.initialized();
        }

        public initialized(): void {
            this.classList.remove("initializing");
        }
    };
}

export interface HasEopSlots {
    resolveSlot: <R extends HTMLElement>(name: string) => Maybe<R>;
    resolveSlots: <R extends HTMLElement>(name: string) => R[];
}

const EOP_SLOT_ATTRIBUTE = "eop-slot";
const EOP_SLOT_RESOLVED_ATTRIBUTE = "eop-slot-resolved";

// eslint-disable-next-line @typescript-eslint/naming-convention
export function WithEopSlots<T extends Constructor<UnLitElement>>(Base: T): T & Constructor<HasEopSlots> {
    return class extends Base implements HasEopSlots {

        private slots: HTMLElement[];
        private slotsInitialized: boolean = false;

        public connectedCallback(): void {
            super.connectedCallback();
            if (!this.slotsInitialized) {
                this.slots = Array.from(this.querySelectorAll<HTMLElement>(`[${EOP_SLOT_ATTRIBUTE}]`))
                    .filter(slot => slot.parentElement === this);
                this.slots.forEach(slot => slot.remove()); // not in DOM anymore, but still in memory, ready to be put into place by rendering
                this.slotsInitialized = true;
            }
        }

        public resolveSlot<R extends HTMLElement>(name: string): Maybe<R> {
            const resolvedSlot = Array.from(this.slots)
                .find(slot => slot.getAttribute(EOP_SLOT_ATTRIBUTE) === name) as R;
            if (resolvedSlot) {
                resolvedSlot.setAttribute(EOP_SLOT_RESOLVED_ATTRIBUTE, "true");
            }
            return resolvedSlot;
        }

        public resolveSlots<R extends HTMLElement>(name: string): R[] {
            const resolvedSlots = Array.from(this.slots)
                .filter(slot => slot.getAttribute(EOP_SLOT_ATTRIBUTE) === name)
                .map(slot => slot as R);
            resolvedSlots.forEach(slot => slot.setAttribute(EOP_SLOT_RESOLVED_ATTRIBUTE, "true"));
            return resolvedSlots;
        }
    };
}